import React, { useState, useEffect } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Badge,
  FormFeedback,
} from "reactstrap"
import Select from "react-select"
import axios from "axios"
import {
  createEmailReportConfig,
  updateEmailReportConfig,
  testEmailReport,
} from "../../helpers/email_report_helper"
import toastr from "toastr"

const EmailReportModal = ({ isOpen, toggle, config, onSuccess }) => {
  const [companies, setCompanies] = useState([])
  const [formData, setFormData] = useState({
    company_id: "",
    email_recipients: [""],
    schedule_frequency: "daily",
    schedule_time: "08:00",
    enabled: true,
    include_alerts: true,
    include_errors: true,
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [testing, setTesting] = useState(false)

  // Fetch companies
  useEffect(() => {
    if (isOpen) {
      fetchCompanies()
    }
  }, [isOpen])

  // Set form data when editing
  useEffect(() => {
    if (config) {
      setFormData({
        company_id: config.company_id?._id || "",
        email_recipients: config.email_recipients || [""],
        schedule_frequency: config.schedule_frequency || "daily",
        schedule_time: config.schedule_time || "08:00",
        enabled: config.enabled !== undefined ? config.enabled : true,
        include_alerts: config.include_alerts !== undefined ? config.include_alerts : true,
        include_errors: config.include_errors !== undefined ? config.include_errors : true,
      })
    } else {
      // Reset form for new config
      setFormData({
        company_id: "",
        email_recipients: [""],
        schedule_frequency: "daily",
        schedule_time: "08:00",
        enabled: true,
        include_alerts: true,
        include_errors: true,
      })
    }
    setErrors({})
  }, [config, isOpen])

  const fetchCompanies = async () => {
    try {
      const baseURL = process.env.REACT_APP_STATUS === "DEV"
        ? process.env.REACT_APP_DEV_URL
        : process.env.REACT_APP_STATUS === "STAGE"
        ? process.env.REACT_APP_STAGE_URL
        : process.env.REACT_APP_PROD_URL

      const token = localStorage.getItem("authUser")
        ? JSON.parse(localStorage.getItem("authUser")).token
        : null

      const response = await axios.get(
        `${baseURL}/api/v1/companies/company-list`
      )
      setCompanies(response.data || [])
    } catch (error) {
      console.error("Error fetching companies:", error)
      toastr.error("Failed to load companies")
    }
  }

  const handleCompanyChange = (selectedOption) => {
    setFormData({ ...formData, company_id: selectedOption.value })
    setErrors({ ...errors, company_id: "" })
  }

  const handleEmailChange = (index, value) => {
    const newEmails = [...formData.email_recipients]
    newEmails[index] = value
    setFormData({ ...formData, email_recipients: newEmails })
    setErrors({ ...errors, email_recipients: "" })
  }

  const addEmailField = () => {
    setFormData({
      ...formData,
      email_recipients: [...formData.email_recipients, ""],
    })
  }

  const removeEmailField = (index) => {
    const newEmails = formData.email_recipients.filter((_, i) => i !== index)
    setFormData({ ...formData, email_recipients: newEmails })
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.company_id) {
      newErrors.company_id = "Please select a company"
    }

    const validEmails = formData.email_recipients.filter(email => email.trim() !== "")
    if (validEmails.length === 0) {
      newErrors.email_recipients = "At least one email recipient is required"
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      const invalidEmails = validEmails.filter(email => !emailRegex.test(email))
      if (invalidEmails.length > 0) {
        newErrors.email_recipients = "Please enter valid email addresses"
      }
    }

    if (!formData.schedule_time) {
      newErrors.schedule_time = "Schedule time is required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)

      // Filter out empty emails
      const validEmails = formData.email_recipients.filter(email => email.trim() !== "")

      const data = {
        ...formData,
        email_recipients: validEmails,
      }

      if (config) {
        // Update existing config
        await updateEmailReportConfig(formData.company_id, data)
        toastr.success("Email report configuration updated successfully")
      } else {
        // Create new config
        await createEmailReportConfig(data)
        toastr.success("Email report configuration created successfully")
      }

      onSuccess(true)
    } catch (error) {
      console.error("Error saving config:", error)
      toastr.error(error.response?.data?.error || "Failed to save configuration")
    } finally {
      setLoading(false)
    }
  }

  const handleTestEmail = async () => {
    if (!formData.company_id) {
      toastr.error("Please select a company first")
      return
    }

    const validEmails = formData.email_recipients.filter(email => email.trim() !== "")
    if (validEmails.length === 0) {
      toastr.error("Please enter at least one email address")
      return
    }

    try {
      setTesting(true)
      await testEmailReport(formData.company_id, validEmails[0])
      toastr.success(`Test email sent to ${validEmails[0]}`)
    } catch (error) {
      console.error("Error sending test email:", error)
      toastr.error("Failed to send test email")
    } finally {
      setTesting(false)
    }
  }

  const companyOptions = companies.map(company => ({
    value: company._id,
    label: company.name,
  }))

  const selectedCompany = companyOptions.find(
    option => option.value === formData.company_id
  )

  return (
    <Modal isOpen={isOpen} toggle={() => toggle(false)} size="lg">
      <ModalHeader toggle={() => toggle(false)}>
        {config ? "Edit Email Report Configuration" : "Add Email Report Configuration"}
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <Row>
            <Col md={12}>
              <FormGroup>
                <Label for="company_id">
                  Company <span className="text-danger">*</span>
                </Label>
                <Select
                  id="company_id"
                  value={selectedCompany}
                  onChange={handleCompanyChange}
                  options={companyOptions}
                  placeholder="Select company..."
                  isDisabled={!!config}
                  className={errors.company_id ? "is-invalid" : ""}
                />
                {errors.company_id && (
                  <div className="invalid-feedback d-block">{errors.company_id}</div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <FormGroup>
                <Label>
                  Email Recipients <span className="text-danger">*</span>
                </Label>
                {formData.email_recipients.map((email, index) => (
                  <div key={index} className="d-flex mb-2">
                    <Input
                      type="email"
                      placeholder="recipient@example.com"
                      value={email}
                      onChange={(e) => handleEmailChange(index, e.target.value)}
                      invalid={!!errors.email_recipients}
                      className="me-2"
                    />
                    {formData.email_recipients.length > 1 && (
                      <Button
                        type="button"
                        color="danger"
                        size="sm"
                        onClick={() => removeEmailField(index)}
                      >
                        <i className="bx bx-x"></i>
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  color="link"
                  size="sm"
                  onClick={addEmailField}
                  className="p-0"
                >
                  <i className="bx bx-plus me-1"></i>
                  Add another email
                </Button>
                {errors.email_recipients && (
                  <div className="invalid-feedback d-block">{errors.email_recipients}</div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="schedule_frequency">
                  Schedule Frequency <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  id="schedule_frequency"
                  value={formData.schedule_frequency}
                  onChange={(e) =>
                    setFormData({ ...formData, schedule_frequency: e.target.value })
                  }
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly (Monday)</option>
                  <option value="monthly">Monthly (1st of month)</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="schedule_time">
                  Schedule Time <span className="text-danger">*</span>
                </Label>
                <Input
                  type="time"
                  id="schedule_time"
                  value={formData.schedule_time}
                  onChange={(e) =>
                    setFormData({ ...formData, schedule_time: e.target.value })
                  }
                  invalid={!!errors.schedule_time}
                />
                <FormFeedback>{errors.schedule_time}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <FormGroup check>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) =>
                      setFormData({ ...formData, enabled: e.target.checked })
                    }
                  />
                  {" "}Enabled
                </Label>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup check>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={formData.include_alerts}
                    onChange={(e) =>
                      setFormData({ ...formData, include_alerts: e.target.checked })
                    }
                  />
                  {" "}Include Alerts
                </Label>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup check>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={formData.include_errors}
                    onChange={(e) =>
                      setFormData({ ...formData, include_errors: e.target.checked })
                    }
                  />
                  {" "}Include Errors
                </Label>
              </FormGroup>
            </Col>
          </Row>

          <div className="alert alert-info mt-3 mb-0">
            <i className="bx bx-info-circle me-2"></i>
            Reports will be generated and sent to R2 storage. Email will contain a download link.
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            color="secondary"
            outline
            onClick={handleTestEmail}
            disabled={testing || loading}
          >
            {testing ? "Sending..." : "Send Test Email"}
          </Button>
          <Button
            type="button"
            color="secondary"
            onClick={() => toggle(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={loading}>
            {loading ? "Saving..." : config ? "Update" : "Create"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default EmailReportModal
